import { useQuery } from '@apollo/client';
import axios from 'axios';
import { useFormik } from 'formik';
import moment from 'moment';
import React, { useState } from 'react';
import { useCookies } from 'react-cookie';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { fetchClient, JSONClient } from '../../api/bootstrap';
import { createEbayView } from '../../api/ebay';
import { TidalButton } from '../../components/common/button';
import TidalCountInput from '../../components/common/input/TidalCountInput';
import TidalInputField from '../../components/common/input/TidalInputField';
import TidalTextBox from '../../components/common/input/TidalTextBox';
import { Box } from '../../components/Flex';
import GraphQLErrorText from '../../components/GraphQLErrorText';
import Loading from '../../components/Loading';
import TidalDashboardHeader from '../../components/tidal-dashboard/common/header';
import TidalDashboardEbayViewsLogElement from '../../components/tidal-dashboard/ebay-views/TidalDashboardEbayViewsLog';
import { COOKIE } from '../../constants';
import useAuthOrRedirect from '../../hooks/useAuthOrRedirect';
import { PROFILE } from '../../lib/graphql/queries';

const TidalDashboardEbayViewsWrapper = styled.div`
  width: 100%;
`;

const TidalDashboardEbayViewsGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  margin-top: 30px;
  @media only screen and (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const TidalDashboardEbayViewsTitle = styled.p`
  font-weight: 700;
  font-size: 21px;
  line-height: 25px;
  margin-bottom: 6.5px;
  color: ${(props) => props.theme.colors.text[2]};
`;

const TidalDashboardEbayViewsForm = styled.div`
  border-right: 1px solid ${(props) => props.theme.colors.borderColors[0]};
  padding-right: 16px;
  padding-bottom: 32px;
  @media only screen and (max-width: 1100px) {
    padding-right: 0;
    border-right: 0;
  }
`;

const TidalDashboardEbayViewsFormFooter = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.borderColors[0]};
  padding-top: 27px;
  margin-top: 27px;
  display: grid;
  grid-template-columns: 1fr 189px;
  justify-content: flex-end;
`;

const TidalDashboardEbayViewsLogs = styled.div`
  padding-left: 16px;
  height: 82vh;
  overflow: auto;
  @media only screen and (max-width: 1100px) {
    border-top: 1px solid ${(props) => props.theme.colors.borderColors[0]};
    padding-left: 0;
    padding-top: 32px;
    margin-top: 60px;
  }
`;

const TidalDashboardEbayViewsLogsHeader = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 189px;
  margin-bottom: 23px;
  position: sticky;
  top: 0;
  padding-bottom: 8px;
  background-color: #ffffff;
  @media only screen and (max-width: 400px) {
    grid-template-columns: 1fr;
    grid-gap: 8px 0;
  }
`;

interface MyFormValues {
  url: string;
  amount: string;
}

const TidalDashboardEbayViews = (): JSX.Element => {
  const { loading, data, error } = useQuery(PROFILE);

  const [url, setURL] = useState('');
  const [amount, setAmount] = useState(1);
  const [proxies, setProxies] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [cookies, _, removeCookie] = useCookies([COOKIE.ACCESS_TOKEN]);

  const readAllChunks = async (readableStream) => {
    const reader = readableStream.getReader();

    let done, value;
    while (!done) {
      ({ value, done } = await reader.read());
      if (done) {
        return;
      }
      const valueBody = new TextDecoder().decode(value).split('\n');
      valueBody.forEach((v) => {
        setLogs((logs) => [
          ...logs,
          {
            type:
              v.includes('error') || v.includes('failed')
                ? 'error'
                : v.includes('Sent')
                ? 'success'
                : 'info',
            text: v,
          },
        ]);
      });
    }
  };

  const handleCreateEbayViews = async () => {
    if (url.length == 0) {
      toast('Please enter a webhook URL', {
        type: 'error',
      });
      return;
    }
    if (amount == 0) {
      toast('Please enter a webhook amount', {
        type: 'error',
      });
      return;
    }

    if (amount < 1 || amount > 2000) {
      toast('Please enter a webhook amount greater than 0 and less than 2000', {
        type: 'error',
      });
      return;
    }

    setIsSaving(true);
    try {
      setLogs((logs) => [
        ...logs,
        {
          type: 'info',
          text: `[${moment().format('h:mm:ss:sss a')}] Authenticated`,
        },
      ]);
      setLogs((logs) => [
        ...logs,
        {
          type: 'info',
          text: `[${moment().format('h:mm:ss:sss a')}] Getting links`,
        },
      ]);

      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          proxies: proxies.split('\n'),
          url,
          amount: amount,
          timezoneOffset: new Date().getTimezoneOffset(),
        }),
      };
      const res = await fetchClient(
        `${process.env.BACKEND_URI}/dashboard/ebay`,
        requestOptions,
      );
      const results = res.body;
      await readAllChunks(results);
    } catch (e) {
      setLogs((logs) => [
        ...logs,
        {
          type: 'error',
          text: `[${new Date().toTimeString().split(' ')[0]}] ${e}`,
        },
      ]);
    }
    setIsSaving(false);
  };

  const validate = (values: MyFormValues) => {
    const errors: MyFormValues = {
      url: '',
      amount: '',
    };
    if (!values.url) {
      errors.url = 'Required';
    }
    if (parseInt(values.amount) < 1) {
      errors.amount = 'Quantity should be greater than 1';
    }
    if (parseInt(values.amount) > 2000) {
      errors.amount = 'Quantity should be less than 2000';
    }
    if (!values.amount) {
      errors.amount = 'Required';
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: {
      url,
      amount: amount.toString(),
    },
    validate,
    onSubmit: (values) => {},
  });

  if (!useAuthOrRedirect()) return null;
  if (loading) return <Loading />;
  if (error) return <GraphQLErrorText error={error} />;

  return (
    <TidalDashboardEbayViewsWrapper>
      <TidalDashboardHeader title="Ebay Views" showSettings={false} tags={[]} />
      <TidalDashboardEbayViewsGrid>
        <TidalDashboardEbayViewsForm>
          <TidalDashboardEbayViewsTitle>
            Listing Link
          </TidalDashboardEbayViewsTitle>
          <TidalInputField
            label="Paste your listing URL below"
            placeholder="Enter URL"
            isRequired={true}
            name="url"
            value={url}
            onBlur={(e) => {
              formik.handleBlur(e);
            }}
            onChange={(e) => {
              formik.handleChange(e);
              setURL(e.target.value);
            }}
          />
          {formik.touched.url && (
            <p className="tidal-error">{formik.errors.url}</p>
          )}

          <Box height={22} />
          <TidalDashboardEbayViewsTitle>Proxies</TidalDashboardEbayViewsTitle>
          <TidalTextBox
            height={280}
            label="Paste proxies for the best results"
            placeholder="ip:port:user:pass"
            onChange={(e) => setProxies(e.target.value)}
          />
          <Box height={22} />
          <TidalDashboardEbayViewsTitle>
            View Quantity
          </TidalDashboardEbayViewsTitle>
          <TidalCountInput
            label="Up to 2,000 views"
            placeholder="0"
            value={amount.toString()}
            name="amount"
            onBlur={(e) => {
              formik.handleBlur(e);
            }}
            onAction={async (e) => {
              try {
                try {
                  await formik.setFieldTouched('amount');
                  await formik.setFieldValue('amount', e, true);
                  await formik.validateField('amount');
                } catch (e) {
                  console.log(e);
                }
              } catch (e) {}
            }}
            onChange={(e) => {
              formik.handleChange(e);
              setAmount(e.target.value);
            }}
          />
          {formik.touched.amount && (
            <p className="tidal-error">{formik.errors.amount}</p>
          )}
          <TidalDashboardEbayViewsFormFooter>
            <div />
            <TidalButton
              onClick={handleCreateEbayViews}
              isLoading={isSaving}
              disabled={formik.errors.url != '' || formik.errors.amount != ''}
              type="info"
              text="Start Views"
              tint={false}
            />
          </TidalDashboardEbayViewsFormFooter>
        </TidalDashboardEbayViewsForm>
        <TidalDashboardEbayViewsLogs>
          <TidalDashboardEbayViewsLogsHeader>
            <TidalDashboardEbayViewsTitle>
              <p>Action logs</p>
            </TidalDashboardEbayViewsTitle>
            <TidalButton
              type="error"
              text="Clear logs"
              tint={false}
              onClick={() => {
                setLogs([]);
              }}
            />
          </TidalDashboardEbayViewsLogsHeader>
          {logs.map((log, index) => (
            <div key={index}>
              <TidalDashboardEbayViewsLogElement
                type={log.type}
                text={log.text}
              />
            </div>
          ))}
          {/* <TidalDashboardEbayViewsLogElement
            type="info"
            text="[1:38:13.498 PM] — Authenticated"
          />
          <TidalDashboardEbayViewsLogElement
            type="info"
            text="[1:38:13.498 PM] — Getting links"
          /> */}
        </TidalDashboardEbayViewsLogs>
      </TidalDashboardEbayViewsGrid>
    </TidalDashboardEbayViewsWrapper>
  );
};

export default TidalDashboardEbayViews;
